import React from 'react';

import { Box, Flex } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { UploadFileComp } from 'components/UploadFileComp';
import { useParseUploadEmployeeListCsv } from 'components/Views/Hooks/dataHooks';
import { useViewPageName } from 'components/Views/Hooks/useViewPageName';
import { useDispatch } from 'react-redux';
import { setCSVUploadFileName, useViewActions } from 'reducers/viewsSlice';

import { sampleEmployeeSelectionFileURI } from 'constants/ProjectConstants';
import { sendUploadCsvInNames } from 'constants/mixpanelHelpers/viewsEvents';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';

import { Divider } from 'uiToolkitV1/Divider';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

export const UploadEmployees = ({
  setIdentifiedEmployeesData,
  setNonIdentifiedEmployeesData,
  nonIdentifiedEmployeesData,
  identifiedEmployeesData,
}) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const operationType = useViewActions();

  const postUploadDataInfoComp = nonIdentifiedEmployeesData?.length > 0 && (
    <>
      <TextBodyMedium color="text.g3" mt={4}>
        We couldn't find accounts for {nonIdentifiedEmployeesData.length} out of{' '}
        {nonIdentifiedEmployeesData.length + identifiedEmployeesData.length} email id
        {nonIdentifiedEmployeesData.length > 1 ? 's' : ''}!
      </TextBodyMedium>
      <TextBodyMedium color="text.g3" mb={2}>
        Please check the following for typos in the uploaded file:
      </TextBodyMedium>
      <TextBodyMedium color="text.g3">{nonIdentifiedEmployeesData.join(', ')}</TextBodyMedium>
    </>
  );

  const page = useViewPageName();

  const dataSetter = (data) => {
    const { identifiedEmailsResponse, nonIdentifiedEmails } = data;
    const noOfUsersUploaded = identifiedEmailsResponse.length;
    setIdentifiedEmployeesData(identifiedEmailsResponse);
    setNonIdentifiedEmployeesData(nonIdentifiedEmails);
    sendUploadCsvInNames({ eventProperties: { page, noOfUsersUploaded, operationType } });
  };

  const handleFileSelect = (file) => {
    dispatch(setCSVUploadFileName(file.name));
  };

  const uploadInstructionComp = (
    <Box borderRadius="4px" bg="neutral.F5F7F9" p={4} w="100%">
      <TextBodyMedium bold>Instructions on how to prepare the CSV file for Upload</TextBodyMedium>
      <ul style={{ paddingLeft: '20px' }}>
        <li>First row should contain the word "email", indicating type of value</li>
        <li>Subsequent rows in the CSV should contain only 1 person’s email Id</li>
        <li>Download and check the sample CSV from "Download Sample Template" button above for reference.</li>
      </ul>
    </Box>
  );

  return (
    <Flex flexDir="column" p="0 8px" flexGrow={1} height="100%">
      <Box p={3}>
        <TextBodyMedium bold>Upload</TextBodyMedium>
      </Box>

      <Divider />
      <UploadFileComp
        heading=" Upload and select list of people"
        subHeading="Please upload list of people you want to select for and follow the instructions for successful upload."
        sampleFileURI={sampleEmployeeSelectionFileURI}
        dataSetter={dataSetter}
        postUploadDataInfoComp={postUploadDataInfoComp}
        uploadFileHandler={useParseUploadEmployeeListCsv}
        onSelectFile={handleFileSelect}
        uploadInstructionComp={uploadInstructionComp}
        retryUseQueryInvalidator={() => {
          queryClient.invalidateQueries([REACT_QUERY_KEYS.views.csvEmployees]);
        }}
      />
    </Flex>
  );
};
